import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { httpErrorInterceptor } from '@/core/interceptors/http-error.interceptor';
import { ApplicationConfig, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { GlobalErrorHandler } from '@/core/services/global-error-handler.service';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, TitleStrategy, withEnabledBlockingInitialNavigation, withInMemoryScrolling } from '@angular/router';
import { AppTitleStrategy } from '@/core/layout/app-title.strategy';
import { AppInitializerService, initializeAppFactory } from '@/core/auth/services/app-initializer.service';
import { providePrimeNG } from 'primeng/config';
import { appRoutes } from './app.routes';
import { appThemePreset } from '@/core/theme/app-theme-preset';
import { authConfig } from '@/core/auth/auth.config';
import { provideAuth, AbstractSecurityStorage } from 'angular-auth-oidc-client';
import { LocalStorageService } from '@/core/auth/services/local-storage.service';
import { provideStore, provideState } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { AuthEffects } from '@/core/auth/store/auth.effects';
import { authReducer } from '@/core/auth/store/auth.reducer';
import { currentUserState } from '@/store/current-user/current-user.reducer';
import { CurrentUserEffects } from '@/store/current-user/current-user.effects';
import { opportunityReducer } from '@/store/opportunity/opportunity.reducer';
import { OpportunityEffects } from '@/store/opportunity/opportunity.effects';
import { taskOpportunityMetricsReducer } from '@/store/task-opportunity-metrics/task-opportunity-metrics.reducer';
import { TaskOpportunityMetricsEffects } from '@/store/task-opportunity-metrics/task-opportunity-metrics.effects';
import { opportunityApprovalsReducer } from '@/store/opportunity-approvals/opportunity-approvals.reducer';
import { OpportunityApprovalsEffects } from '@/store/opportunity-approvals/opportunity-approvals.effects';
import { taskReducer } from '@/store/task/task.reducer';
import { TaskEffects } from '@/store/task/task.effects';
import { opportunityLinksReducer } from '@/store/opportunity-links/opportunity-links.reducer';
import { OpportunityLinksEffects } from '@/store/opportunity-links/opportunity-links.effects';
import { opportunityFilesReducer } from '@/store/opportunity-files/opportunity-files.reducer';
import { OpportunityFilesEffects } from '@/store/opportunity-files/opportunity-files.effects';
import { adminReducers, adminEffects } from '@/core/admin/store';
import { commentReducer } from '@/store/comment/comment.reducer';
import { CommentEffects } from '@/store/comment/comment.effects';
import { inProgressDashboardReducer } from '@/store/in-progress-dashboard/in-progress-dashboard.reducer';
import { InProgressDashboardEffects } from '@/store/in-progress-dashboard/in-progress-dashboard.effects';
import { reviewHistoryReducer } from '@/store/review-history/review-history.reducer';
import { ReviewHistoryEffects } from '@/store/review-history/review-history.effects';
import { MessageService } from 'primeng/api';
import { provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts';

const MyPreset = appThemePreset;

// Define root reducers (all reducers except for 'admin')
const rootReducers = {
  auth: authReducer,
  opportunity: opportunityReducer,
  currentUserState: currentUserState,
  taskOpportunityMetrics: taskOpportunityMetricsReducer,
  opportunityApprovals: opportunityApprovalsReducer,
  tasks: taskReducer,
  opportunityLinks: opportunityLinksReducer,
  opportunityFiles: opportunityFilesReducer,
  comment: commentReducer, // Add comment reducer
};

export const appConfig: ApplicationConfig = {
  providers: [
    // Global error handler for chunk loading failures
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // Core providers first
    provideHttpClient(withFetch(), withInterceptors([httpErrorInterceptor])),
    provideAuth(authConfig),
    // Use localStorage instead of sessionStorage to share tokens across tabs
    { provide: AbstractSecurityStorage, useClass: LocalStorageService },
    provideRouter(
      appRoutes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
      withEnabledBlockingInitialNavigation()
    ),
    // Document title from the same route data as the breadcrumb and masthead (SCRUM-595)
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    provideAnimationsAsync(),
    providePrimeNG({
      ripple: false,
      inputStyle: 'filled',
      theme: {
        preset: MyPreset,
        options: { darkModeSelector: false },
      },
    }),
    MessageService, // Provide MessageService here
    provideEchartsCore({ echarts }), // Provide echarts configuration for ngx-echarts
    provideStore(rootReducers), // Provide root reducers
    provideState('admin', adminReducers), // Provide admin feature state
    provideState('inProgressDashboard', inProgressDashboardReducer), // Provide in-progress dashboard feature state
    provideState('reviewHistory', reviewHistoryReducer), // Provide review history feature state
    provideEffects([
      AuthEffects,
      CurrentUserEffects,
      OpportunityEffects,
      TaskOpportunityMetricsEffects,
      OpportunityApprovalsEffects,
      TaskEffects,
      OpportunityLinksEffects,
      OpportunityFilesEffects,
      ...adminEffects,
      CommentEffects, // Add CommentEffects
      InProgressDashboardEffects, // Add InProgressDashboardEffects
      ReviewHistoryEffects, // Add ReviewHistoryEffects
    ]),
    provideStoreDevtools({
      maxAge: 25, // Keeps last 25 states in the history
      logOnly: false, // Set to true for production mode to disable state modifications
      autoPause: true, // Pauses recording when the DevTools are not open
    }),
    // APP_INITIALIZER runs after other providers are configured
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      deps: [AppInitializerService],
      multi: true
    },
  ],
};
